// frontend/src/pages/Dashboard.tsx
// Enhanced Dashboard with clickable navigation, live updates, and interactive features
// Fixed: Proper portfolio refresh, consistent risk thresholds (High >6, Medium >3, Low <=3)

import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  Button,
  Chip,
  Alert,
  IconButton,
  Tooltip,
  LinearProgress,
  Paper,
  Skeleton,
} from '@mui/material';
import { Grid } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WarningIcon from '@mui/icons-material/Warning';
import FolderIcon from '@mui/icons-material/Folder';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MapIcon from '@mui/icons-material/Map';
import AddIcon from '@mui/icons-material/Add';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AssessmentIcon from '@mui/icons-material/Assessment';
import HistoryIcon from '@mui/icons-material/History';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ExploreIcon from '@mui/icons-material/Explore';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import PortfolioHealthScore from '../components/Portfolio/PortfolioHealthScore';
import PredictiveRiskAlerts from '../components/Portfolio/PredictiveRiskAlerts';

const API_URL = process.env.REACT_APP_API_URL || 'https://spatial-risk-api.onrender.com/api';

// ============================================
// CONSISTENT RISK THRESHOLDS (used across platform)
// High Risk: > 6 (score 6.1 - 10)
// Medium Risk: > 3 and <= 6 (score 3.1 - 6.0)
// Low Risk: <= 3 (score 0 - 3.0)
// ============================================
const RISK_THRESHOLDS = {
  HIGH: 6,    // > 6 is high risk
  MEDIUM: 3,  // > 3 and <= 6 is medium risk
  // <= 3 is low risk
};

const RISK_COLORS = {
  HIGH: '#EF4444',
  MEDIUM: '#F59E0B',
  LOW: '#10B981',
};

interface PortfolioSummary {
  id: string;
  name: string;
  item_count: number;
  total_value: number;
  avg_risk: number | string;
  total_event_count_10: number;
  total_event_damage_10: number;
  items?: any[];
  created_at?: string;
  updated_at?: string;
}

interface DashboardStats {
  totalPortfolios: number;
  totalLocations: number;
  totalValue: number;
  avgRisk: number;
  highRiskLocations: number;
  mediumRiskLocations: number;
  lowRiskLocations: number;
}

interface WeatherAlertSummary {
  totalAlerts: number;
  activeAlerts: number;
  highSeverityCount: number;
  affectedLocations: number;
}

interface ActivityItem {
  id: string;
  type: 'portfolio_created' | 'portfolio_analyzed' | 'alert_received' | 'location_added';
  title: string;
  description: string;
  timestamp: Date;
  portfolioId?: string;
}

// Helper functions for risk categorization
const getRiskCategory = (risk: number): 'HIGH' | 'MEDIUM' | 'LOW' => {
  if (risk > RISK_THRESHOLDS.HIGH) return 'HIGH';
  if (risk > RISK_THRESHOLDS.MEDIUM) return 'MEDIUM';
  return 'LOW';
};

const getRiskColor = (risk: number): string => {
  return RISK_COLORS[getRiskCategory(risk)];
};

const getRiskLabel = (risk: number): string => {
  const category = getRiskCategory(risk);
  return category.charAt(0) + category.slice(1).toLowerCase();
};

const formatLargeNumber = (value: number): string => {
  if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
};

export default function Dashboard() {
  const [portfolios, setPortfolios] = useState<PortfolioSummary[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [weatherAlerts, setWeatherAlerts] = useState<WeatherAlertSummary | null>(null);
  const [recentActivity, setRecentActivity] = useState<ActivityItem[]>([]);
  const [aggregatedItems, setAggregatedItems] = useState<any[]>([]);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = localStorage.getItem('token');

  // Fetch dashboard data - always fetch fresh from API, no caching
  const fetchDashboardData = useCallback(async () => {
    try {
      const userId = user.id || 'demo';
      console.log('Dashboard: Fetching fresh portfolio data...');
      console.log('Dashboard: User ID being sent:', userId);
      console.log('Dashboard: Full user object:', user);
      
      // Always fetch fresh data from API
      const response = await axios.get(`${API_URL}/portfolios`, {
        params: { userId },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const portfolioData: PortfolioSummary[] = response.data || [];
      console.log('Dashboard: Received', portfolioData.length, 'portfolios');
      console.log('Dashboard: Portfolio names:', portfolioData.map(p => p.name));
      
      setPortfolios(portfolioData);
      
      // Calculate statistics from fresh data
      let totalLocations = 0;
      let totalValue = 0;
      let totalRiskSum = 0;
      let totalItemCount = 0;
      let highRisk = 0;
      let mediumRisk = 0;
      let lowRisk = 0;
      
      for (const p of portfolioData) {
        const itemCount = p.item_count || p.items?.length || 0;
        const portfolioValue = parseFloat(String(p.total_value || 0));
        const avgRisk = typeof p.avg_risk === 'string' 
          ? parseFloat(p.avg_risk) 
          : (p.avg_risk || 0);
        
        totalLocations += itemCount;
        totalValue += portfolioValue;
        
        if (itemCount > 0 && !isNaN(avgRisk)) {
          totalRiskSum += avgRisk * itemCount;
          totalItemCount += itemCount;
        }
        
        // Calculate risk distribution from individual items if available
        if (p.items && Array.isArray(p.items)) {
          for (const item of p.items) {
            const risk = parseFloat(String(item.risk_score || 0));
            if (risk > RISK_THRESHOLDS.HIGH) highRisk++;
            else if (risk > RISK_THRESHOLDS.MEDIUM) mediumRisk++;
            else lowRisk++;
          }
        } else {
          // Fallback: estimate from portfolio average risk
          if (avgRisk > RISK_THRESHOLDS.HIGH) highRisk += itemCount;
          else if (avgRisk > RISK_THRESHOLDS.MEDIUM) mediumRisk += itemCount;
          else lowRisk += itemCount;
        }
      }
      
      const avgRisk = totalItemCount > 0 ? totalRiskSum / totalItemCount : 0;
      
      const statsData: DashboardStats = {
        totalPortfolios: portfolioData.length,
        totalLocations,
        totalValue,
        avgRisk,
        highRiskLocations: highRisk,
        mediumRiskLocations: mediumRisk,
        lowRiskLocations: lowRisk,
      };
      
      console.log('Dashboard: Calculated stats:', statsData);
      setStats(statsData);
      
      // Generate recent activity from portfolios
      const activities: ActivityItem[] = portfolioData
        .filter((p) => p.created_at)
        .sort((a, b) => new Date(b.created_at!).getTime() - new Date(a.created_at!).getTime())
        .slice(0, 5)
        .map((p) => ({
          id: p.id,
          type: 'portfolio_created' as const,
          title: `Portfolio "${p.name}" created`,
          description: `${p.item_count || 0} locations, ${formatLargeNumber(parseFloat(String(p.total_value || 0)))} value`,
          timestamp: new Date(p.created_at!),
          portfolioId: p.id,
        }));
      
      setRecentActivity(activities);
      setError('');
      
      // Fetch detailed items for Portfolio Health Score (for first 5 portfolios)
      const allItems: any[] = [];
      for (const p of portfolioData.slice(0, 5)) {
        try {
          const analysisResponse = await axios.get(
            `${API_URL}/portfolios/${p.id}/analysis`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (analysisResponse.data?.items) {
            allItems.push(...analysisResponse.data.items);
          }
        } catch (analysisErr) {
          console.log('Could not fetch analysis for portfolio:', p.id);
        }
      }
      setAggregatedItems(allItems);
      console.log('Dashboard: Aggregated', allItems.length, 'items for health score');
      
    } catch (err: any) {
      console.error('Dashboard fetch error:', err);
      setError(err.response?.data?.error || 'Failed to load dashboard data');
    }
  }, [user.id, token]);

  // Fetch weather alerts for all portfolios
  const fetchWeatherAlerts = useCallback(async () => {
    if (portfolios.length === 0) {
      setWeatherAlerts({ totalAlerts: 0, activeAlerts: 0, highSeverityCount: 0, affectedLocations: 0 });
      return;
    }

    try {
      let totalAlerts = 0;
      let activeAlerts = 0;
      let highSeverityCount = 0;
      let affectedLocations = 0;

      // Fetch alerts for each portfolio (limit to first 3 for performance)
      for (const portfolio of portfolios.slice(0, 3)) {
        try {
          const response = await axios.get(
            `${API_URL}/alerts/portfolio/${portfolio.id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          if (response.data?.summary) {
            totalAlerts += response.data.summary.totalAlerts || 0;
            activeAlerts += response.data.summary.activeAlerts || 0;
            highSeverityCount += response.data.summary.highSeverityCount || 0;
            affectedLocations += response.data.summary.affectedLocations || 0;
          }
        } catch (alertErr) {
          console.log('Could not fetch alerts for portfolio:', portfolio.id);
        }
      }

      setWeatherAlerts({ totalAlerts, activeAlerts, highSeverityCount, affectedLocations });
    } catch (err) {
      console.error('Error fetching weather alerts:', err);
    }
  }, [portfolios, token]);

  // Initial data load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchDashboardData();
      setLoading(false);
    };
    loadData();
  }, [fetchDashboardData]);

  // Fetch weather alerts when portfolios change
  useEffect(() => {
    if (portfolios.length > 0) {
      fetchWeatherAlerts();
    }
  }, [portfolios, fetchWeatherAlerts]);

  // Listen for portfolio changes (create/delete from Portfolio page)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'portfolioUpdated') {
        console.log('Dashboard: Detected portfolio update via storage event');
        fetchDashboardData();
      }
    };

    // Listen for custom events within the same tab
    const handlePortfolioUpdate = () => {
      console.log('Dashboard: Detected portfolio update via custom event');
      fetchDashboardData();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('portfolioUpdated', handlePortfolioUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('portfolioUpdated', handlePortfolioUpdate);
    };
  }, [fetchDashboardData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDashboardData();
    await fetchWeatherAlerts();
    setTimeout(() => setRefreshing(false), 500);
  };

  // Navigate to Portfolio page with Weather Alerts tab selected
  const navigateToWeatherAlerts = () => {
    navigate('/portfolio', { state: { openTab: 'weather-alerts' } });
  };

  // Navigate to Map in Discovery mode with a random city
  const navigateToCityExplorer = () => {
    navigate('/map', { state: { mode: 'discovery', loadRandomCity: true } });
  };

  // Risk distribution data for pie chart
  const riskDistributionData = stats ? [
    { name: 'High Risk', value: stats.highRiskLocations, color: RISK_COLORS.HIGH },
    { name: 'Medium Risk', value: stats.mediumRiskLocations, color: RISK_COLORS.MEDIUM },
    { name: 'Low Risk', value: stats.lowRiskLocations, color: RISK_COLORS.LOW },
  ].filter(d => d.value > 0) : [];

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="text" width={300} height={50} />
          <Skeleton variant="text" width={200} height={30} />
        </Box>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map(i => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#111827', mb: 0.5 }}>
            Welcome back, {user.name || 'User'}
          </Typography>
          <Typography variant="body1" sx={{ color: '#6B7280' }}>
            Here's an overview of your climate risk portfolio
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Tooltip title="Refresh data">
            <IconButton 
              onClick={handleRefresh} 
              disabled={refreshing}
              sx={{ 
                backgroundColor: '#F3F4F6',
                '&:hover': { backgroundColor: '#E5E7EB' }
              }}
            >
              <RefreshIcon sx={{ 
                animation: refreshing ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' }
                }
              }} />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/portfolio')}
            sx={{ 
              backgroundColor: '#00D4FF',
              '&:hover': { backgroundColor: '#00B8E6' }
            }}
          >
            New Portfolio
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Weather Alerts Banner */}
      {weatherAlerts && weatherAlerts.activeAlerts > 0 && (
        <Alert 
          severity="warning" 
          sx={{ mb: 3, cursor: 'pointer' }}
          onClick={navigateToWeatherAlerts}
          action={
            <Button color="inherit" size="small" endIcon={<ArrowForwardIcon />}>
              View Details
            </Button>
          }
        >
          <strong>{weatherAlerts.activeAlerts} active weather alerts</strong> affecting {weatherAlerts.affectedLocations} locations in your portfolios
          {weatherAlerts.highSeverityCount > 0 && (
            <Chip 
              label={`${weatherAlerts.highSeverityCount} High Severity`} 
              size="small" 
              color="error" 
              sx={{ ml: 2 }}
            />
          )}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Main Content - Left Column */}
        <Grid item xs={12} lg={8}>
          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Portfolios Card */}
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                sx={{ 
                  height: '100%', 
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 }
                }}
                onClick={() => navigate('/portfolio')}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ 
                      width: 48, 
                      height: 48, 
                      borderRadius: 2, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      backgroundColor: 'rgba(0, 212, 255, 0.1)'
                    }}>
                      <FolderIcon sx={{ color: '#00D4FF', fontSize: 28 }} />
                    </Box>
                    <ArrowForwardIcon sx={{ color: '#9CA3AF' }} />
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#111827', mb: 0.5 }}>
                    {stats?.totalPortfolios || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6B7280' }}>
                    Active Portfolios
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Locations Card */}
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                sx={{ 
                  height: '100%', 
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 }
                }}
                onClick={() => navigate('/map')}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ 
                      width: 48, 
                      height: 48, 
                      borderRadius: 2, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      backgroundColor: 'rgba(16, 185, 129, 0.1)'
                    }}>
                      <LocationOnIcon sx={{ color: '#10B981', fontSize: 28 }} />
                    </Box>
                    <ArrowForwardIcon sx={{ color: '#9CA3AF' }} />
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#111827', mb: 0.5 }}>
                    {stats?.totalLocations || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6B7280' }}>
                    Total Locations
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Value Card */}
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                sx={{ 
                  height: '100%', 
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 }
                }}
                onClick={() => navigate('/financial-impact')}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ 
                      width: 48, 
                      height: 48, 
                      borderRadius: 2, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      backgroundColor: 'rgba(139, 92, 246, 0.1)'
                    }}>
                      <AttachMoneyIcon sx={{ color: '#8B5CF6', fontSize: 28 }} />
                    </Box>
                    <ArrowForwardIcon sx={{ color: '#9CA3AF' }} />
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#111827', mb: 0.5 }}>
                    {formatLargeNumber(stats?.totalValue || 0)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6B7280' }}>
                    Total Asset Value
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Risk Card */}
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                sx={{ 
                  height: '100%', 
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 }
                }}
                onClick={() => navigate('/portfolio')}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ 
                      width: 48, 
                      height: 48, 
                      borderRadius: 2, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      backgroundColor: `${getRiskColor(stats?.avgRisk || 0)}15`
                    }}>
                      <TrendingUpIcon sx={{ color: getRiskColor(stats?.avgRisk || 0), fontSize: 28 }} />
                    </Box>
                    <Chip 
                      label={getRiskLabel(stats?.avgRisk || 0)}
                      size="small"
                      sx={{ 
                        backgroundColor: `${getRiskColor(stats?.avgRisk || 0)}20`,
                        color: getRiskColor(stats?.avgRisk || 0),
                        fontWeight: 600
                      }}
                    />
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#111827', mb: 0.5 }}>
                    {(stats?.avgRisk || 0).toFixed(1)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6B7280' }}>
                    Average Risk Score
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Portfolio List */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827' }}>
                Your Portfolios
              </Typography>
              <Button 
                size="small" 
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/portfolio')}
              >
                View All
              </Button>
            </Box>

            {portfolios.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <FolderIcon sx={{ fontSize: 48, color: '#D1D5DB', mb: 2 }} />
                <Typography variant="body1" sx={{ color: '#6B7280', mb: 2 }}>
                  No portfolios yet
                </Typography>
                <Button 
                  variant="outlined" 
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/portfolio')}
                >
                  Create Your First Portfolio
                </Button>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {portfolios.slice(0, 4).map((portfolio) => {
                  const avgRisk = typeof portfolio.avg_risk === 'string' 
                    ? parseFloat(portfolio.avg_risk) 
                    : (portfolio.avg_risk || 0);
                  
                  return (
                    <Grid item xs={12} sm={6} key={portfolio.id}>
                      <Card 
                        variant="outlined"
                        sx={{ 
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': { borderColor: '#00D4FF', boxShadow: 2 }
                        }}
                        onClick={() => navigate('/portfolio', { state: { selectedPortfolioId: portfolio.id } })}
                      >
                        <CardContent sx={{ pb: '16px !important' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#111827' }}>
                              {portfolio.name}
                            </Typography>
                            <Chip 
                              label={getRiskLabel(avgRisk)}
                              size="small"
                              sx={{ 
                                backgroundColor: `${getRiskColor(avgRisk)}20`,
                                color: getRiskColor(avgRisk),
                                fontWeight: 600,
                                fontSize: '0.7rem'
                              }}
                            />
                          </Box>
                          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Typography variant="body2" sx={{ color: '#6B7280' }}>
                              <strong>{portfolio.item_count || 0}</strong> locations
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#6B7280' }}>
                              <strong>{formatLargeNumber(parseFloat(String(portfolio.total_value || 0)))}</strong> value
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#6B7280' }}>
                              Risk: <strong style={{ color: getRiskColor(avgRisk) }}>{avgRisk.toFixed(1)}</strong>
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Paper>

          {/* Risk Distribution */}
          {stats && stats.totalLocations > 0 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827', mb: 3 }}>
                Risk Distribution
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ height: 200 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={riskDistributionData}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {riskDistributionData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 2 }}>
                    {riskDistributionData.map((item) => (
                      <Box key={item.name} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: item.color }} />
                        <Typography variant="body2" sx={{ color: '#6B7280' }}>
                          {item.name}: {item.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" sx={{ color: '#111827', fontWeight: 500 }}>
                          High Risk (&gt;6)
                        </Typography>
                        <Typography variant="body2" sx={{ color: RISK_COLORS.HIGH, fontWeight: 600 }}>
                          {stats.highRiskLocations} ({stats.totalLocations > 0 ? ((stats.highRiskLocations / stats.totalLocations) * 100).toFixed(1) : 0}%)
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={stats.totalLocations > 0 ? (stats.highRiskLocations / stats.totalLocations) * 100 : 0} 
                        sx={{ 
                          height: 8, 
                          borderRadius: 4,
                          backgroundColor: '#FEE2E2',
                          '& .MuiLinearProgress-bar': { backgroundColor: RISK_COLORS.HIGH, borderRadius: 4 }
                        }}
                      />
                    </Box>
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" sx={{ color: '#111827', fontWeight: 500 }}>
                          Medium Risk (3.1-6)
                        </Typography>
                        <Typography variant="body2" sx={{ color: RISK_COLORS.MEDIUM, fontWeight: 600 }}>
                          {stats.mediumRiskLocations} ({stats.totalLocations > 0 ? ((stats.mediumRiskLocations / stats.totalLocations) * 100).toFixed(1) : 0}%)
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={stats.totalLocations > 0 ? (stats.mediumRiskLocations / stats.totalLocations) * 100 : 0} 
                        sx={{ 
                          height: 8, 
                          borderRadius: 4,
                          backgroundColor: '#FEF3C7',
                          '& .MuiLinearProgress-bar': { backgroundColor: RISK_COLORS.MEDIUM, borderRadius: 4 }
                        }}
                      />
                    </Box>
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" sx={{ color: '#111827', fontWeight: 500 }}>
                          Low Risk (0-3)
                        </Typography>
                        <Typography variant="body2" sx={{ color: RISK_COLORS.LOW, fontWeight: 600 }}>
                          {stats.lowRiskLocations} ({stats.totalLocations > 0 ? ((stats.lowRiskLocations / stats.totalLocations) * 100).toFixed(1) : 0}%)
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={stats.totalLocations > 0 ? (stats.lowRiskLocations / stats.totalLocations) * 100 : 0} 
                        sx={{ 
                          height: 8, 
                          borderRadius: 4,
                          backgroundColor: '#D1FAE5',
                          '& .MuiLinearProgress-bar': { backgroundColor: RISK_COLORS.LOW, borderRadius: 4 }
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* Portfolio Health Score */}
          {aggregatedItems.length > 0 && (
            <Paper sx={{ p: 3, mt: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827', mb: 3 }}>
                Portfolio Health Assessment
              </Typography>
              <PortfolioHealthScore 
                portfolio={{ 
                  items: aggregatedItems,
                  name: 'All Portfolios',
                  total_value: stats?.totalValue || 0
                }} 
              />
            </Paper>
          )}

          {/* Predictive Risk Alerts */}
          {aggregatedItems.length > 0 && (
            <Paper sx={{ p: 3, mt: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827', mb: 3 }}>
                Predictive Risk Alerts
              </Typography>
              <PredictiveRiskAlerts 
                portfolio={{ 
                  items: aggregatedItems,
                  id: portfolios[0]?.id,
                  name: 'All Portfolios'
                }} 
              />
            </Paper>
          )}
        </Grid>

        {/* Right Column - Activity & Quick Links */}
        <Grid item xs={12} lg={4}>
          {/* Recent Activity */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <HistoryIcon sx={{ color: '#6B7280' }} />
              <Typography variant="h6" sx={{ color: '#111827', fontWeight: 600 }}>
                Recent Activity
              </Typography>
            </Box>
            
            {recentActivity.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {recentActivity.map((activity) => (
                  <Box 
                    key={activity.id}
                    sx={{ 
                      p: 2, 
                      borderRadius: 2, 
                      backgroundColor: '#F9FAFB',
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#F3F4F6' }
                    }}
                    onClick={() => activity.portfolioId && navigate('/portfolio', { state: { selectedPortfolioId: activity.portfolioId } })}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827', mb: 0.5 }}>
                      {activity.title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>
                      {activity.description}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: '#6B7280', textAlign: 'center', py: 2 }}>
                No recent activity
              </Typography>
            )}
          </Paper>

          {/* Quick Links */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ color: '#111827', fontWeight: 600, mb: 2 }}>
              Quick Links
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                fullWidth
                variant="text"
                startIcon={<MapIcon />}
                onClick={() => navigate('/map')}
                sx={{ 
                  justifyContent: 'flex-start', 
                  color: '#374151',
                  py: 1.5,
                  '&:hover': { 
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    color: '#10B981',
                  }
                }}
              >
                Interactive Map
              </Button>
              <Button
                fullWidth
                variant="text"
                startIcon={<AssessmentIcon />}
                onClick={() => navigate('/financial-impact')}
                sx={{ 
                  justifyContent: 'flex-start', 
                  color: '#374151',
                  py: 1.5,
                  '&:hover': { 
                    backgroundColor: 'rgba(139, 92, 246, 0.1)',
                    color: '#8B5CF6',
                  }
                }}
              >
                Financial Impact
              </Button>
              <Button
                fullWidth
                variant="text"
                startIcon={<NotificationsIcon />}
                onClick={navigateToWeatherAlerts}
                sx={{ 
                  justifyContent: 'flex-start', 
                  color: '#374151',
                  py: 1.5,
                  '&:hover': { 
                    backgroundColor: 'rgba(245, 158, 11, 0.1)',
                    color: '#F59E0B',
                  }
                }}
              >
                Weather Alerts
              </Button>
              <Button
                fullWidth
                variant="text"
                startIcon={<ExploreIcon />}
                onClick={navigateToCityExplorer}
                sx={{ 
                  justifyContent: 'flex-start', 
                  color: '#374151',
                  py: 1.5,
                  '&:hover': { 
                    backgroundColor: 'rgba(37, 99, 235, 0.1)',
                    color: '#2563EB',
                  }
                }}
              >
                City Explorer
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}